import { IRouteErrorParams, IRouteErrorResponse } from '@/interfaces/IRouteErrorResponse';
import { z } from 'zod';
import { fromZodError } from 'zod-validation-error';
import { CommonError } from '@common/interfaces/logging/CommonError';
import { CommonErrorCodes } from '@common/interfaces/logging/CommonErrorCodes';

/**
 * Helper error class - we can use this to throw errors and ensure they'll be returned cleanly and consistently
 * to the client as a JSON response with the correct status code.
 */
export class RouteError extends CommonError {
  public readonly isRouteError = true;
  public readonly status: number;
  public readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(response: IRouteErrorParams, status: number = 500) {
    super(CommonErrorCodes.ROUTE_ERROR, response.error, response.name || 'RouteError');
    this.status = status;
    this.fieldErrors = response.fieldErrors;
    Object.setPrototypeOf(this, RouteError.prototype);
  }

  static fromZodError(error: z.ZodError): RouteError {
    const fieldErrors = Object.entries(error.flatten().fieldErrors).reduce(
      (acc, [key, value]) => {
        if (!value) {
          return acc;
        }

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(...value);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    return new RouteError(
      {
        error: fromZodError(error).message,
        fieldErrors,
      },
      400,
    );
  }
}

export function isRouteError(error: unknown): error is RouteError {
  if (!error) {
    return false;
  }
  return (error as RouteError).isRouteError;
}

export const routeErrorSchema: z.ZodType<IRouteErrorResponse> = z.object({
  isRouteErrorResponse: z.literal(true),
  name: z.string(),
  error: z.string(),
  fieldErrors: z.record(z.array(z.string())).optional(),
});
